/**
 * RecurringTab
 * Displays detected recurring transactions (subscriptions) in the Budget screen.
 */

import React from 'react';
import { View, Text, FlatList, StyleSheet, ActivityIndicator } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import RecurringItem from './RecurringItem';
import type { RecurringTransaction } from '../../api/endpoints/budget';

interface Props {
  transactions: RecurringTransaction[];
  isLoading: boolean;
  totalMonthly?: number;
}

export default function RecurringTab({ transactions, isLoading, totalMonthly }: Props) {
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={colors.primary} />
        <Text style={styles.loadingText}>Analyse des abonnements...</Text>
      </View>
    );
  }

  if (transactions.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Icon name="repeat" size={64} color={colors.textSecondary} />
        <Text style={styles.emptyTitle}>Aucun abonnement détecté</Text>
        <Text style={styles.emptyText}>
          Importe tes relevés bancaires pour détecter automatiquement tes prélèvements récurrents.
        </Text>
      </View>
    );
  }

  // Separate expenses from incomes
  const expenses = transactions.filter(t => t.transaction_type === 'expense');
  const incomes = transactions.filter(t => t.transaction_type !== 'expense');

  // Next upcoming debit
  const nextDebit = expenses
    .filter(t => t.next_expected)
    .sort((a, b) => new Date(a.next_expected).getTime() - new Date(b.next_expected).getTime())[0];

  // Bank fees insight
  const bankFees = expenses.filter(t => t.category === 'bank_fees');

  return (
    <View style={styles.container}>
      {/* Header with total */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Abonnements détectés</Text>
        {totalMonthly != null && totalMonthly > 0 && (
          <Text style={styles.headerTotal}>{totalMonthly.toLocaleString('fr-FR')} €/mois</Text>
        )}
      </View>

      {/* Next upcoming debit */}
      {nextDebit && (
        <View style={styles.nextDebitCard}>
          <Icon name="calendar-clock" size={18} color={colors.primary} />
          <Text style={styles.nextDebitText}>
            Prochain prélèvement : <Text style={styles.nextDebitBold}>{nextDebit.description}</Text>{' '}
            ({nextDebit.amount.toLocaleString('fr-FR')} €) le{' '}
            {new Date(nextDebit.next_expected).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
          </Text>
        </View>
      )}

      {/* Expense recurring list */}
      <FlatList
        data={expenses}
        keyExtractor={(item) => item.normalized_key}
        renderItem={({ item }) => <RecurringItem item={item} />}
        scrollEnabled={false}
        ListHeaderComponent={
          expenses.length > 0 ? (
            <Text style={styles.sectionTitle}>Prélèvements ({expenses.length})</Text>
          ) : null
        }
      />

      {/* Income recurring list */}
      {incomes.length > 0 && (
        <FlatList
          data={incomes}
          keyExtractor={(item) => item.normalized_key}
          renderItem={({ item }) => <RecurringItem item={item} />}
          scrollEnabled={false}
          ListHeaderComponent={
            <Text style={styles.sectionTitle}>Revenus récurrents ({incomes.length})</Text>
          }
        />
      )}

      {/* Bank fees insight */}
      {bankFees.length > 0 && (
        <View style={styles.insightCard}>
          <Icon name="lightbulb-outline" size={18} color="#F59E0B" />
          <Text style={styles.insightText}>
            Tu paies {bankFees.reduce((sum, f) => sum + Number(f.amount), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/mois
            en frais bancaires. Une banque en ligne pourrait t'en faire économiser une partie.
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: spacing.xl,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
    gap: spacing.sm,
  },
  loadingText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  emptyContainer: {
    alignItems: 'center',
    padding: spacing.xxl,
    gap: spacing.sm,
  },
  emptyTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  headerTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
  },
  headerTotal: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    color: '#EF4444',
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textSecondary,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  nextDebitCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primaryLight,
    borderRadius: 8,
    padding: spacing.sm,
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  nextDebitText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  nextDebitBold: {
    fontWeight: typography.fontWeight.semibold,
  },
  insightCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FFFBEB',
    borderRadius: 8,
    padding: spacing.sm,
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  insightText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text,
    lineHeight: 20,
  },
});
